import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../lib/constants";

export default function PrivacyPolicy() {
    return (
        <div className="min-h-screen bg-brand-cream bg-cream-grain">
            <header className="border-b border-brand-line bg-paper">
                <div className="mx-auto max-w-[900px] px-5 sm:px-8 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="privacy-home-link">
                        <img src={LOGO_URL} alt="RENOVA360" className="h-9 w-9 object-contain" />
                        <span className="font-display text-2xl text-brand-navy tracking-tight">RENOVA360</span>
                    </Link>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-navy transition-colors"
                        data-testid="privacy-back-link"
                    >
                        <ArrowLeft size={16} /> Torna al login
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-[820px] px-5 sm:px-8 py-12 lg:py-16" data-testid="privacy-page">
                <div className="eyebrow text-brand-gold mb-3">Documento legale</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                    Informativa sulla privacy
                </h1>
                <p className="mt-3 text-sm text-brand-muted">Ultimo aggiornamento: 28 febbraio 2026</p>

                <div className="prose-renova mt-10 space-y-8 text-[15px] leading-7 text-brand-ink/90">
                    <Section title="1. Titolare del trattamento">
                        <p>
                            Il titolare del trattamento dei dati personali raccolti tramite il servizio
                            <strong> RENOVA360 — Food Cost Manager </strong>(il “Servizio”) è:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>MAKAI di Maria Laura Pellino</strong></li>
                            <li>Partita IVA: <strong>09936410969</strong></li>
                            <li>Email di contatto: <a href="mailto:info@renova-foodcost.shop" className="text-brand-navy underline">info@renova-foodcost.shop</a></li>
                            <li>Sito: <a href="https://www.renova-foodcost.shop" className="text-brand-navy underline">www.renova-foodcost.shop</a></li>
                        </ul>
                    </Section>

                    <Section title="2. Tipologie di dati raccolti">
                        <p>Tra i dati personali trattati dal Servizio, in modo autonomo o tramite terze parti, vi sono:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Dati di registrazione: nome, indirizzo email, password (cifrata con bcrypt), nome del ristorante.</li>
                            <li>Dati di accesso tramite Google OAuth (solo quando l’utente sceglie l’accesso con Google): email, nome, foto profilo.</li>
                            <li>Dati operativi inseriti dall’utente: ingredienti, ricette, fornitori, acquisti.</li>
                            <li>Dati di pagamento: gestiti integralmente da <strong>Stripe Payments Europe Ltd.</strong>; il Servizio non memorizza numeri di carta o credenziali bancarie. Vengono memorizzati solo l’ID cliente Stripe e lo stato dell’abbonamento.</li>
                            <li>Dati tecnici: indirizzo IP, user-agent, log di accesso necessari alla sicurezza del Servizio.</li>
                        </ul>
                    </Section>

                    <Section title="3. Finalità e base giuridica">
                        <p>I dati sono trattati per le seguenti finalità:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Erogare il Servizio (esecuzione del contratto — art. 6.1.b GDPR).</li>
                            <li>Gestione di pagamenti, fatturazione e abbonamenti tramite Stripe (obbligo contrattuale e legale — art. 6.1.b/c GDPR).</li>
                            <li>Adempimenti fiscali e contabili (obbligo legale — art. 6.1.c GDPR).</li>
                            <li>Sicurezza dell’infrastruttura, prevenzione frodi, log tecnici (legittimo interesse — art. 6.1.f GDPR).</li>
                        </ul>
                    </Section>

                    <Section title="4. Modalità di trattamento e luogo">
                        <p>
                            I dati sono trattati con strumenti elettronici e adeguate misure di sicurezza (cifratura in transito tramite HTTPS,
                            password con hashing bcrypt, autenticazione JWT, accesso amministrativo limitato).
                        </p>
                        <p className="mt-2">
                            I dati sono conservati su server gestiti dai provider tecnici Emergent (hosting infrastrutturale) e
                            MongoDB Atlas. Stripe può trattare alcuni dati anche al di fuori dell’Unione Europea, sulla base delle
                            <em> Standard Contractual Clauses </em>approvate dalla Commissione Europea.
                        </p>
                    </Section>

                    <Section title="5. Periodo di conservazione">
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Dati account: per tutta la durata dell’abbonamento e fino a 12 mesi dopo la cessazione, salvo cancellazione anticipata su richiesta.</li>
                            <li>Dati di fatturazione: 10 anni come richiesto dalla normativa fiscale italiana.</li>
                            <li>Log tecnici: massimo 12 mesi.</li>
                        </ul>
                    </Section>

                    <Section title="6. Destinatari dei dati">
                        <p>I dati possono essere comunicati esclusivamente a:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>Stripe Payments Europe Ltd.</strong> — gestione pagamenti e abbonamenti.</li>
                            <li><strong>Google LLC</strong> — solo se l’utente sceglie il login con Google (autenticazione OAuth).</li>
                            <li><strong>Emergent</strong> — hosting e infrastruttura cloud del Servizio.</li>
                            <li>Autorità pubbliche, su legittima richiesta.</li>
                        </ul>
                        <p className="mt-2">I dati non vengono ceduti, venduti o diffusi a terzi per finalità di marketing.</p>
                    </Section>

                    <Section title="7. Diritti dell’interessato">
                        <p>In ogni momento l’utente può esercitare i diritti previsti dagli artt. 15-22 GDPR:</p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>Accesso, rettifica, cancellazione dei propri dati.</li>
                            <li>Limitazione od opposizione al trattamento.</li>
                            <li>Portabilità dei dati.</li>
                            <li>Reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-brand-navy underline">www.garanteprivacy.it</a>).</li>
                        </ul>
                        <p className="mt-2">
                            Per esercitare i propri diritti scrivere a
                            {" "}<a href="mailto:info@renova-foodcost.shop" className="text-brand-navy underline">info@renova-foodcost.shop</a>.
                        </p>
                    </Section>

                    <Section title="8. Modifiche">
                        <p>
                            Il Titolare si riserva di modificare la presente informativa in qualsiasi momento, dandone evidenza
                            agli utenti tramite questa pagina. Si consiglia di consultarla periodicamente.
                        </p>
                    </Section>

                    <div className="border-t border-brand-line pt-6 text-sm text-brand-muted">
                        Vedi anche la nostra <Link to="/cookie-policy" className="text-brand-navy underline" data-testid="privacy-to-cookie">Cookie Policy</Link>.
                    </div>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-display text-2xl text-brand-navy mb-2">{title}</h2>
            <div>{children}</div>
        </section>
    );
}
